// ============================================================
// editform.js — Formulaire de modification d'un parc existant
// Composant de classe contrôlé, pré-rempli avec les données
// du parc passé en prop (client). Logique identique à addform.js
// sauf que l'état initial reprend les valeurs existantes du parc.
// À la soumission, envoie un POST /modif/<id> avec le token Auth0.
// Props :
//   client    — objet parc à modifier (name, ControlDate, URL, lat, lng)
//   authtoken — token Bearer Auth0 (peut être une Promise)
// ============================================================

import React, { Component } from 'react'
import { Form, Button } from 'react-bootstrap';
import axios from 'axios';

class EditForm extends Component {
    constructor(props) {
        super(props)
        // Pré-remplissage de l'état avec les données existantes du parc
        this.state = {
            ControlDate: this.props.client.ControlDate, // Date du dernier contrôle
            name: this.props.client.name,               // Nom du parc
            URL: this.props.client.URL,                 // URL Google Maps actuelle
            lat: this.props.client.lat,                 // Latitude actuelle
            lng: this.props.client.lng,                 // Longitude actuelle
            URLError:"",          // Message d'erreur affiché sous le champ URL
            nameError: "",        // (réservé) message erreur nom
            ControlDateError: "", // (réservé) message erreur date
            URLValid: true,       // true dès le départ car l'URL existante est valide
            userControlId: this.props.client.userControlId || 0,
            }

        this.handleChange = this.handleChange.bind(this)
        this.handleURL = this.handleURL.bind(this)
        this.handleSubmit = this.handleSubmit.bind(this)
    };

    // Soumission : vérifie l'URL puis envoie les modifications à l'API.
    // En cas de succès : rafraîchit la liste et ferme la modale (onRefresh/onHide).
    handleSubmit = async event => {
        event.preventDefault()
        if(this.state.URLValid === true){
            Promise.resolve(this.props.authtoken).then((token) => {
                // Envoie uniquement les champs utiles à l'endpoint de modification
                axios.post('/modif/' + this.props.client.id,
                { name: this.state.name, URL: this.state.URL, lat: this.state.lat, lng: this.state.lng, ControlDate: this.state.ControlDate, userControlId: parseInt(this.selectedExpertId(), 10) },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
                .then(() => {
                    if (this.props.onRefresh) this.props.onRefresh();
                    if (this.props.onHide) this.props.onHide();
                })
                .catch(err => {
                    console.error('Erreur modification parc:', err)
                    // Affiche la raison du refus renvoyée par le serveur (validation)
                    const data = err.response && err.response.data
                    const details = data && data.errors ? data.errors.join('\n') : (data && data.error) || err.message
                    alert("Modification refusée :\n" + details)
                })
            })
        }
        else {
            // L'URL n'est pas valide — informe l'utilisateur
            alert("veuillez verifier votre URL")
        }
    }

    // Expert sélectionné : premier expert de la liste si le parc n'en a pas
    selectedExpertId() {
        const experts = this.props.experts || []
        return this.state.userControlId || (experts[0] && experts[0].id) || 0
    }

    // Gestionnaire générique pour tous les champs (nom, date, expert) :
    // utilise le "computed property name" ES6 pour mettre à jour
    // dynamiquement la bonne clé du state selon le name de l'input.
    handleChange = event => {
        this.setState({
            // La clé est déterminée par l'attribut name de l'élément
            [event.target.name]: event.target.value
        })
    }

    // Gestionnaire spécifique pour le champ URL Google Maps :
    // extrait latitude et longitude selon deux formats d'URL possibles.
    handleURL = async event => {
        var URL = event.target.value;
        if(URL.toString().includes('google.com/maps') || URL.toString().includes('google.fr/maps')) {
            let Lat = null, Lng = null;

            // Format 1 : https://maps.google.com/.../place/Nom/@lat,lng,zoom/...
            // Coordonnées après le caractère '@' dans l'URL
            if (URL.includes('@')) {
                const atPart = URL.split('@')[1];
                if (atPart) {
                    Lat = atPart.split(',')[0];
                    Lng = atPart.split(',')[1];
                }
            }

            // Format 2 : coordonnées dans le paramètre data de l'URL
            // sous la forme !3d<lat>!4d<lng> (nouveau format Google Maps)
            if (!Lat || !Lng) {
                const latMatch = URL.match(/!3d(-?\d+\.\d+)/);
                const lngMatch = URL.match(/!4d(-?\d+\.\d+)/);
                if (latMatch && lngMatch) {
                    Lat = latMatch[1];
                    Lng = lngMatch[1];
                }
            }

            if (Lat && Lng) {
                // Coordonnées trouvées : URL valide
                this.setState({ URL: URL, lat: Lat, lng: Lng, URLValid: true, URLError: "" });
            } else {
                // URL Google Maps mais coordonnées introuvables
                this.setState({ URLError: "Impossible d'extraire les coordonnées depuis cette URL", URLValid: false });
            }
        }
        else{
            // Pas une URL Google Maps
            this.setState({ URLError:"l'URL n'est pas une URL Google Maps", URLValid: false })
        }
    }

    // Rendu du formulaire pré-rempli avec les données existantes du parc.
    // defaultValue pré-remplit les champs sans bloquer les modifications.
    render (){
        return (
            <Form id="formid" onSubmit={this.handleSubmit}>
                {/* Champ nom du parc (pré-rempli avec le nom actuel) */}
                <Form.Group>
                    <Form.Label>Nom</Form.Label>
                    <Form.Control
                        required
                        name="name"
                        type="string"
                        onChange={this.handleChange}
                        defaultValue={this.state.name}
                    />
                </Form.Group>

                {/* Champ date de contrôle (pré-rempli avec la date actuelle) */}
                <Form.Group>
                    <Form.Label>Date de controle</Form.Label>
                    <Form.Control
                        required
                        name="ControlDate"
                        type="date"
                        onChange={this.handleChange}
                        defaultValue={this.state.ControlDate}
                    />
                </Form.Group>

                {/* Sélecteur de l'expert (pré-rempli avec l'expert du parc) */}
                <Form.Group>
                    <Form.Label>Controle fait par:</Form.Label>
                    <Form.Control
                        onChange={this.handleChange}
                        as="select"
                        name="userControlId"
                        value={this.selectedExpertId()}
                        className="rounded-0 shadow dateControl">
                        {(this.props.experts || []).map(function(e) {
                            return <option key={e.id} value={e.id}>{e.name}</option>;
                        })}
                    </Form.Control>
                </Form.Group>

                {/* Champ URL Google Maps avec validation et affichage des coordonnées */}
                <Form.Group>
                    <Form.Label>URL</Form.Label>
                    <Form.Control
                        required
                        name="URL"
                        type="string"
                        onChange={this.handleURL}
                        defaultValue={this.state.URL}
                        />
                    {/* Message d'erreur si l'URL n'est pas reconnue */}
                    <div style={{color: "red", fontSize: 12}}>{this.state.URLError}</div>
                    {/* Confirmation verte avec lat/lng extraites si l'URL est valide */}
                    {this.state.URLValid && (
                        <div style={{color: "green", fontSize: 12, marginTop: 4}}>
                            Lat : {this.state.lat} — Lng : {this.state.lng}
                        </div>
                    )}
                </Form.Group>

                {/* Bouton de soumission */}
                <Button style={{ margin: "5px" }} variant="primary" type = "submit">
                    Enregistrer
                </Button>
            </Form>
        )
    }
}

export default EditForm
